/**
 * SF DocAI extraction: upload + declarative response→form mapping.
 *
 * The Salesforce templates return `fields: [{name, value, confidence?}]`
 * where the names are template-driven and NOT stable across template edits
 * ("company_name" became "Legal Company Name" between runs; one field is
 * spelled "Sharholders"). Everything response-specific therefore lives in the
 * TEMPLATES registry below: adding or renaming a form field is one entry
 * here plus one render entry in the screen. Unmapped API fields are warned
 * about so template drift stays visible.
 *
 * Live field schemas captured 2026-07-22 — see
 * sf-api-test/test-documents/FIELD-MAPPING.md and the response fixtures.
 */

import { apiUpload } from './client'
import {
  type ApexSubField,
  isApexList,
  normaliseValue,
  parseApexList,
} from './apexParse'

/** Per-field AI confidence (0..1), keyed by form field name. */
export type ConfidenceMap = Record<string, number>

export type DocumentType =
  | 'certificate_of_incorporation'
  | 'license'
  | 'certificate_of_incumbency'
  | 'passport'
  | 'proof_of_address'

/** Backend document slots (documents table CHECK, migration 00004). */
export type UploadSlot =
  | 'certificate_of_incorporation'
  | 'license'
  | 'incumbency'
  | 'shareholders_certificate'
  | 'directors_certificate'
  | 'shareholder_passport'
  | 'shareholder_proof_of_address'
  | 'director_passport'

/**
 * Slot → SF documentType. The lane-B certificates run through the incumbency
 * template: dedicated certificate_of_shareholders / certificate_of_directors
 * types do not exist in the org (404 TEMPLATE_NOT_FOUND, verified 2026-07-22)
 * and the incumbency template extracts both document kinds fine — the
 * irrelevant list simply comes back empty.
 */
export const SLOT_DOCUMENT_TYPE: Record<UploadSlot, DocumentType> = {
  certificate_of_incorporation: 'certificate_of_incorporation',
  license: 'license',
  incumbency: 'certificate_of_incumbency',
  shareholders_certificate: 'certificate_of_incumbency',
  directors_certificate: 'certificate_of_incumbency',
  shareholder_passport: 'passport',
  director_passport: 'passport',
  shareholder_proof_of_address: 'proof_of_address',
}

export interface ExtractedShareholder {
  kind: 'person' | 'holding'
  firstName: string // '' for holding companies
  lastName: string
  companyName: string // Shareholder Name; the company name when holding
  ownershipPct?: number
  confidence: ConfidenceMap
}

export interface ExtractedDirector {
  firstName: string
  lastName: string
  confidence: ConfidenceMap
}

export interface DocAIExtraction {
  documentType: DocumentType
  /** formKey → normalized value; keys absent when the template returned null. */
  fields: Record<string, string>
  confidence: ConfidenceMap
  shareholders?: ExtractedShareholder[]
  directors?: ExtractedDirector[]
  /** Non-empty scalar fields + list rows; drives the success copy. */
  extractedFieldCount: number
  /** API field names that matched nothing in the registry (template drift). */
  unmappedApiFields: string[]
}

// ─── Registry ────────────────────────────────────────────────────────────────

interface FieldSpec {
  formKey: string
  /** Known template names for this field, past and present. */
  apiNames: string[]
  transform?: (raw: string) => string
  /**
   * The template still extracts this field but the form no longer collects it.
   * Matched (so it isn't reported as template drift) and then dropped.
   */
  discard?: boolean
}

interface ListSpec {
  apiNames: string[]
  target: 'shareholders' | 'directors'
}

interface TemplateSpec {
  fields: FieldSpec[]
  lists?: ListSpec[]
}

/** dd/mm/yyyy or dd.mm.yyyy → yyyy-mm-dd; ISO dates pass through. */
const MONTH_NAMES: Record<string, string> = {
  january: '01',
  february: '02',
  march: '03',
  april: '04',
  may: '05',
  june: '06',
  july: '07',
  august: '08',
  september: '09',
  october: '10',
  november: '11',
  december: '12',
  jan: '01',
  feb: '02',
  mar: '03',
  apr: '04',
  jun: '06',
  jul: '07',
  aug: '08',
  sep: '09',
  sept: '09',
  oct: '10',
  nov: '11',
  dec: '12',
}

/**
 * Normalizes a DocAI-extracted date string to ISO (yyyy-mm-dd). Certificates
 * print dates in whatever format the issuing registry uses — dd/mm/yyyy and
 * dd.mm.yyyy were the only two ever observed until a real submission
 * (2026-08-20, Kameliya's Finateqs Corp. certificate) came back
 * "9th September 2013": a written-out date the original regex-only
 * implementation didn't handle, so it passed through unchanged all the way
 * to the Salesforce create call, which rejected the whole submission with
 * JSON_PARSER_ERROR since Date_of_incorporation__c requires strict ISO.
 * Handles, in order: already-ISO (no-op), dd/mm/yyyy or dd.mm.yyyy
 * (original numeric case), and "9th September 2013" / "9 September 2013" /
 * "September 9, 2013" / "9-Sep-2013" (written month name, any day-ordinal
 * suffix, comma optional, dash or space between day and month).
 *
 * Exported for direct unit testing — date-format edge cases are much
 * clearer to test against the raw string than by crafting a full DocAI
 * extraction fixture per format.
 */
export function toIsoDate(raw: string): string {
  const trimmed = raw.trim()

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed

  const numeric = trimmed.match(/^(\d{2})[./](\d{2})[./](\d{4})$/)
  if (numeric) return `${numeric[3]}-${numeric[2]}-${numeric[1]}`

  // "9th September 2013" / "9 September, 2013" / "9-Sep-2013"
  const dayMonthYear = trimmed.match(
    /^(\d{1,2})(?:st|nd|rd|th)?[\s-]+([A-Za-z]+)[\s,-]+(\d{4})$/
  )
  if (dayMonthYear) {
    const month = MONTH_NAMES[dayMonthYear[2].toLowerCase()]
    if (month) {
      const day = dayMonthYear[1].padStart(2, '0')
      return `${dayMonthYear[3]}-${month}-${day}`
    }
  }

  // "September 9, 2013" / "September 9th 2013"
  const monthDayYear = trimmed.match(
    /^([A-Za-z]+)[\s]+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/
  )
  if (monthDayYear) {
    const month = MONTH_NAMES[monthDayYear[1].toLowerCase()]
    if (month) {
      const day = monthDayYear[2].padStart(2, '0')
      return `${monthDayYear[3]}-${month}-${day}`
    }
  }

  return raw
}

export const TEMPLATES: Record<DocumentType, TemplateSpec> = {
  certificate_of_incorporation: {
    fields: [
      { formKey: 'legalCompanyName', apiNames: ['Legal Company Name', 'company_name'] },
      { formKey: 'registrationNumber', apiNames: ['Registration Number', 'company_number'] },
      { formKey: 'incorporationDate', apiNames: ['Date of Incorporation', 'incorporation_date'], transform: toIsoDate },
      { formKey: 'countryOfIncorporation', apiNames: ['Country of Incorporation', 'incorporation_place'] },
    ],
  },
  license: {
    fields: [
      { formKey: 'licenseType', apiNames: ['License Type'] },
      { formKey: 'licenseNumber', apiNames: ['License Number'] },
      // Removed from the form (MAF 22-07 scope cut) but still returned by the
      // DocAI template — matched and discarded so they don't read as drift.
      { formKey: 'licenseRegulator', apiNames: ['Name of Regulator', 'Regulator'], discard: true },
      { formKey: 'licenseExpiryDate', apiNames: ['License Expiration Date', 'License Expiry Date'], discard: true },
    ],
  },
  certificate_of_incumbency: {
    fields: [
      // Registered office moved to Company Details (manual entry, defaulted
      // from Country of incorporation) — matched here and discarded so the
      // SF template still returning them doesn't read as drift.
      { formKey: 'registeredAddressStreet', apiNames: ['Registered Office Street'], discard: true },
      { formKey: 'registeredAddressCity', apiNames: ['Registered Office City'], discard: true },
      { formKey: 'registeredAddressCountry', apiNames: ['Registered Office Country'], discard: true },
    ],
    lists: [
      // 'Sharholders' is the template's own typo — match both spellings.
      { apiNames: ['Sharholders', 'Shareholders'], target: 'shareholders' },
      { apiNames: ['Directors'], target: 'directors' },
    ],
  },
  passport: {
    fields: [
      { formKey: 'firstName', apiNames: ['firstName', 'First Name'] },
      { formKey: 'lastName', apiNames: ['lastName', 'Last Name'] },
      { formKey: 'dob', apiNames: ['date Of Birth', 'Date of Birth'], transform: toIsoDate },
      { formKey: 'passportNumber', apiNames: ['passport Number', 'Passport Number'] },
      { formKey: 'nationality', apiNames: ['Nationality'] },
      // Note: the template returns the issuing authority (e.g. "UKPA"),
      // not a country — surfaced as-is, editable in the form.
      { formKey: 'placeOfIssue', apiNames: ['Place of Issue'] },
    ],
  },
  proof_of_address: {
    fields: [{ formKey: 'registeredAddress', apiNames: ['Proof of Address', 'Address'] }],
  },
}

/** Strips a trailing percent sign: "99%" → "99". */
function stripPercent(raw: string): string {
  return raw.trim().replace(/%$/, '').trim()
}

/** Sub-field registry for rows of the incumbency Sharholders list. */
const SHAREHOLDER_ROW_FIELDS: FieldSpec[] = [
  { formKey: 'firstName', apiNames: ['First Name'] },
  { formKey: 'lastName', apiNames: ['Last Name'] },
  { formKey: 'companyName', apiNames: ['Shareholder Name'] },
  { formKey: 'ownershipPct', apiNames: ['Ownership Percentage'], transform: stripPercent },
]

const DIRECTOR_ROW_FIELDS: FieldSpec[] = [
  { formKey: 'firstName', apiNames: ['First Name'] },
  { formKey: 'lastName', apiNames: ['Last Name'] },
]

// ─── Mapping ─────────────────────────────────────────────────────────────────

/** Raw SF extract response, passed through the Go proxy untouched. */
interface RawExtractResponse {
  status?: string
  documentType?: string
  fields?: Array<{ name?: string; value?: unknown; confidence?: unknown }>
}

/** Tolerant field-name matching: lowercase, strip everything non-alphanumeric. */
function normalizeKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function bySpec<T extends FieldSpec | ListSpec>(specs: T[]): Map<string, T> {
  const index = new Map<string, T>()
  for (const spec of specs) {
    for (const apiName of spec.apiNames) index.set(normalizeKey(apiName), spec)
  }
  return index
}

/** Applies the row-field registry to one parsed Apex row. */
function mapRow(row: ApexSubField[], specs: FieldSpec[]): {
  values: Record<string, string>
  confidence: ConfidenceMap
} {
  const index = bySpec(specs)
  const values: Record<string, string> = {}
  const confidence: ConfidenceMap = {}
  for (const sub of row) {
    const spec = index.get(normalizeKey(sub.name))
    if (!spec) continue
    const value = normaliseValue(sub.value)
    if (value === undefined) continue
    values[spec.formKey] = spec.transform ? spec.transform(value) : value
    if (sub.confidence != null) confidence[spec.formKey] = sub.confidence
  }
  return { values, confidence }
}

function toShareholder(row: ApexSubField[]): ExtractedShareholder {
  const { values, confidence } = mapRow(row, SHAREHOLDER_ROW_FIELDS)
  // Holding-company signature (verified on the Pipbull fixture): person names
  // are null while Shareholder Name carries the company name.
  const isPerson = Boolean(values.firstName || values.lastName)
  const pct = values.ownershipPct !== undefined ? parseFloat(values.ownershipPct) : NaN
  return {
    kind: isPerson ? 'person' : 'holding',
    firstName: values.firstName ?? '',
    lastName: values.lastName ?? '',
    companyName: values.companyName ?? '',
    ownershipPct: Number.isFinite(pct) ? pct : undefined,
    confidence,
  }
}

function toDirector(row: ApexSubField[]): ExtractedDirector {
  const { values, confidence } = mapRow(row, DIRECTOR_ROW_FIELDS)
  return {
    firstName: values.firstName ?? '',
    lastName: values.lastName ?? '',
    confidence,
  }
}

/**
 * Normalizes a raw SF extract response into the typed, form-keyed shape the
 * screens consume. Missing values (JSON null or the string "null" — both
 * occur) are skipped entirely: no fields entry, no confidence entry.
 */
export function mapExtraction(raw: unknown, documentType: DocumentType): DocAIExtraction {
  const spec = TEMPLATES[documentType]
  const fieldIndex = bySpec(spec.fields)
  const listIndex = bySpec(spec.lists ?? [])

  const extraction: DocAIExtraction = {
    documentType,
    fields: {},
    confidence: {},
    extractedFieldCount: 0,
    unmappedApiFields: [],
  }
  if (spec.lists) {
    for (const list of spec.lists) extraction[list.target] = []
  }

  const response = (raw ?? {}) as RawExtractResponse
  for (const field of response.fields ?? []) {
    if (!field.name) continue
    const key = normalizeKey(field.name)

    const listSpec = listIndex.get(key)
    if (listSpec && isApexList(field.value)) {
      const rows = parseApexList(field.value) ?? []
      if (listSpec.target === 'shareholders') {
        extraction.shareholders = rows.map(toShareholder)
      } else {
        extraction.directors = rows.map(toDirector)
      }
      extraction.extractedFieldCount += rows.length
      continue
    }

    const fieldSpec = fieldIndex.get(key)
    if (!fieldSpec) {
      extraction.unmappedApiFields.push(field.name)
      continue
    }
    if (fieldSpec.discard) continue
    const value = normaliseValue(typeof field.value === 'string' ? field.value : undefined)
    if (value === undefined) continue
    extraction.fields[fieldSpec.formKey] = fieldSpec.transform
      ? fieldSpec.transform(value)
      : value
    extraction.extractedFieldCount += 1
    const conf = typeof field.confidence === 'number' ? field.confidence : undefined
    if (conf != null) extraction.confidence[fieldSpec.formKey] = conf
  }

  if (extraction.unmappedApiFields.length > 0) {
    console.warn(
      `[docai] ${documentType}: unmapped API fields (template drift?):`,
      extraction.unmappedApiFields,
    )
  }
  return extraction
}

// ─── Upload ──────────────────────────────────────────────────────────────────

interface ProcessResponse {
  success: boolean
  filename: string
  size: number
  processingTime: number
  extractedData: unknown
  document: {
    id: string
    slot: string
    slot_index: number | null
    filename: string
    mime_type: string
    size_bytes: number
    upload_status: string
  } | null
}

/**
 * Uploads one document for extraction and persistence. The backend stores the
 * raw response against the applicant's draft under `slot`; the mapped
 * extraction comes back for immediate form fill.
 */
export async function processDocument(
  file: File,
  slot: UploadSlot,
  slotIndex?: number,
): Promise<DocAIExtraction> {
  const documentType = SLOT_DOCUMENT_TYPE[slot]
  const form = new FormData()
  form.append('document', file)
  form.append('documentType', documentType)
  form.append('slot', slot)
  if (slotIndex !== undefined) form.append('slot_index', String(slotIndex))

  const response = await apiUpload<ProcessResponse>('/api/v1/applicant/documents/process', form)
  return mapExtraction(response.extractedData, documentType)
}
